import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const BuyerAuthContext = createContext({});

export const useBuyerAuth = () => {
  const context = useContext(BuyerAuthContext);
  if (!context) {
    throw new Error('useBuyerAuth must be used within BuyerAuthProvider');
  }
  return context;
};

export const BuyerAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId, userMetadata = null) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.eq('role', 'buyer')
          ?.maybeSingle();

        if (!error && data) {
          setUserProfile(data);
        } else if (userMetadata && userMetadata.role === 'buyer') {
          // If profile not found but user has buyer role in metadata,
          // try to create the profile record
          const profileData = {
            id: userId,
            full_name: userMetadata.full_name || userMetadata.fullName,
            role: 'buyer',
            email: userMetadata.email,
            company_name: userMetadata.company_name || userMetadata.companyName,
            phone: userMetadata.phone,
            country: userMetadata.country,
            business_license_number: userMetadata.business_license_number || userMetadata.businessLicenseNumber,
            import_license_number: userMetadata.import_license_number || userMetadata.importLicenseNumber,
            preferred_language: userMetadata.preferred_language || userMetadata.preferredLanguage || 'en',
            account_type: userMetadata.account_type || userMetadata.accountType || 'individual'
          };

          const { data: newData, error: insertError } = await supabase
            ?.from('user_profiles')
            ?.insert(profileData)
            ?.select()
            ?.single();

          if (!insertError && newData) {
            setUserProfile(newData);
          } else {
            setUserProfile(profileData);
          }
        }
      } catch (error) {
        console.error('Profile load error:', error);
      } finally {
        setProfileLoading(false);
        setLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setProfileLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const handleAuthChange = (session) => {
      if (!mounted) return;

      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        profileOperations.load(currentUser.id, currentUser.user_metadata);
      } else {
        profileOperations.clear();
      }
    };

    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      handleAuthChange(session);
    });

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      handleAuthChange(session);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        profileOperations?.clear();
      }
      return { error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };

    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select()
        ?.single();
      if (!error) setUserProfile(data);
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user && (userProfile?.role === 'buyer' || user?.user_metadata?.role === 'buyer')
  };

  return (
    <BuyerAuthContext.Provider value={value}>
      {children}
    </BuyerAuthContext.Provider>
  );
};